import { useCallback, useEffect, useRef, useState } from "react";

export interface WpCredentials {
  password: string;
  userLogin: string;
}

export interface UseWpAuthorizeReturn {
  isAuthorizing: boolean;
  error: string | null;
  authorize: (wpUrl: string) => Promise<WpCredentials>;
}

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

export function useWpAuthorize(): UseWpAuthorizeReturn {
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);

  const cleanup = useCallback(() => {
    if (messageHandlerRef.current) {
      window.removeEventListener("message", messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
      popupRef.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const authorize = useCallback(async (wpUrl: string): Promise<WpCredentials> => {
    return new Promise((resolve, reject) => {
      setIsAuthorizing(true);
      setError(null);

      // Generate a callback URL for the console
      const consoleUrl = window.location.origin;
      const callbackUrl = `${consoleUrl}/wp-callback`;

      // Build the authorization URL
      const authUrl = new URL(`${API_URL}/auth/wp-authorize`);
      authUrl.searchParams.set("callbackUrl", callbackUrl);
      authUrl.searchParams.set("wpUrl", wpUrl);

      // Open popup
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      popupRef.current = window.open(
        authUrl.toString(),
        "wp-auth",
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popupRef.current || popupRef.current.closed) {
        setIsAuthorizing(false);
        setError("Popup was blocked. Please allow popups for this site and try again.");
        reject(new Error("Popup blocked"));
        return;
      }

      // Listen for messages from the popup
      const handleMessage = (event: MessageEvent) => {
        // Verify the message is from the popup
        if (event.source !== popupRef.current) return;

        // Verify the message is from the expected origin
        if (event.origin !== window.location.origin) return;

        if (event.data.type === "wp-auth-success") {
          cleanup();
          setIsAuthorizing(false);
          resolve(event.data.credentials);
        } else if (event.data.type === "wp-auth-error") {
          cleanup();
          setIsAuthorizing(false);
          setError(event.data.error || "Authorization failed");
          reject(new Error(event.data.error || "Authorization failed"));
        }
      };

      messageHandlerRef.current = handleMessage;
      window.addEventListener("message", handleMessage);

      // Set a timeout (5 minutes)
      const timeout = setTimeout(() => {
        cleanup();
        setIsAuthorizing(false);
        setError("Authorization timed out. Please try again.");
        reject(new Error("Authorization timed out"));
      }, 5 * 60 * 1000);

      // Clear timeout when popup is closed
      const checkPopupClosed = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(checkPopupClosed);
          clearTimeout(timeout);
          // If we get here without resolving, check if there was an error
          if (isAuthorizing) {
            setIsAuthorizing(false);
            if (!error) {
              setError("Authorization was cancelled.");
              reject(new Error("Authorization cancelled"));
            }
          }
        }
      }, 1000);
    });
  }, [cleanup]);

  return { isAuthorizing, error, authorize };
}
